// In-app "Join Class" page ("/app/live/:id/join") — keeps the student/
// teacher inside the application instead of opening the Zoom/Google Meet
// link in a new browser tab, where the raw URL would sit in the address
// bar and be trivial to copy and forward to someone who was never
// enrolled. Students call the existing enrollment-checked /attend endpoint
// (which records attendance and hands back meeting_link); teachers/admins
// just fetch the class. The meeting link is only ever used as an iframe
// src or a click handler here — it is never rendered as plain/copyable text.
//
// Google Meet always blocks iframe embedding (X-Frame-Options/
// frame-ancestors), so LiveClasses.jsx never routes Meet links through
// this page at all — it opens them directly in a new tab instead. This
// page is for other providers (e.g. Zoom); the "Open in a new tab"
// fallback button only renders for Meet links as a defensive case (e.g.
// if this page is reached directly for one), never as an automatic
// redirect.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { normalizeExternalUrl, isGoogleMeetLink } from "@/lib/video";
import { ArrowLeft, ExternalLink } from "lucide-react";
import dayjs from "dayjs";

export default function LiveClassJoinPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [live, setLive] = useState(null);
  const [meetingLink, setMeetingLink] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        if (user.role === "student") {
          const [attendRes, liveRes] = await Promise.all([
            api.post(`/live-classes/${id}/attend`),
            api.get(`/live-classes/${id}`),
          ]);
          setMeetingLink(attendRes.data.meeting_link || "");
          setLive(liveRes.data);
        } else {
          const r = await api.get(`/live-classes/${id}`);
          setLive(r.data);
          setMeetingLink(r.data.meeting_link || "");
        }
      } catch (err) {
        setError(formatApiError(err));
        toast.error(formatApiError(err));
      }
    };
    load();
  }, [id, user.role]);

  if (error)
    return (
      <div className="space-y-4">
        <p className="text-sm text-red-600 border border-red-200 bg-red-50 px-4 py-3" data-testid="live-class-join-error">{error}</p>
        <Link to="/app/live" className="text-sm font-semibold text-blue-700 hover:underline">← Back to live classes</Link>
      </div>
    );
  if (!live) return <p className="text-sm text-zinc-500" data-testid="live-class-join-loading">Loading class…</p>;

  const normalized = normalizeExternalUrl(meetingLink);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="live-class-join-page">
      <Link to="/app/live" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-950">
        <ArrowLeft className="w-4 h-4" /> Back to live classes
      </Link>

      <div>
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-400">Live Class · {live.subject}</p>
        <h1 className="font-heading text-3xl font-black tracking-tight mt-1" data-testid="live-class-join-title">{live.title}</h1>
        <p className="text-xs text-zinc-500 mt-1">
          {dayjs(live.start_time).format("D MMM YYYY · h:mm A")} · by {live.teacher_name}
        </p>
      </div>

      {normalized ? (
        <>
          <div className="border border-zinc-200 bg-zinc-50">
            <iframe
              src={normalized}
              title="Live class"
              allow="camera; microphone; fullscreen; display-capture; autoplay"
              className="w-full h-[70vh]"
              data-testid="live-class-iframe"
            />
          </div>
          {isGoogleMeetLink(meetingLink) && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between border border-amber-300 bg-amber-50 px-4 py-3">
              <p className="text-xs text-amber-900">
                Google Meet blocks being displayed inside another site for security reasons. If the class above doesn't load, use the button to join in a new tab.
              </p>
              <button
                onClick={() => window.open(normalized, "_blank", "noopener,noreferrer")}
                data-testid="live-class-open-new-tab"
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border border-amber-400 bg-white text-amber-900 hover:bg-amber-100 transition-colors shrink-0"
              >
                Open in a new tab <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-zinc-500" data-testid="live-class-no-link">No meeting link has been set for this class yet.</p>
      )}
    </div>
  );
}
